<template>
  <div class="pos-screen">
    <div class="pos-topbar">
      <div class="brand">
        <div class="brand-icon">
          <i class="fa fa-shopping-basket"></i>
        </div>
        <span class="brand-title">Supershop POS</span>
      </div>
      <div class="topbar-right">
        <div class="warehouse-select">
          <i class="fa fa-industry warehouse-icon"></i>
          <v-select
            placeholder="Select Warehouse"
            v-model="formData.location_id"
            :options="locations"
            label="text"
            :reduce="text => text.id"
            :clearable="false"
          />
        </div>
        <span class="date-chip">
          <i class="fa fa-calendar-o"></i>
          {{ todayDate }}
        </span>
        <button type="button" class="btn exit-btn" @click="goToList">
          <i class="fa fa-sign-out mr-1"></i> Exit
        </button>
      </div>
    </div>

    <div class="pos-body">
      <div class="pos-main">
        <div class="entry-card">
          <TableHeader
            :tableItems="tableItems"
            :additionalProductQuery="additionalProductQuery"
            ref="formHeader"
          />
        </div>

        <div class="items-card">
          <ListTable
            :tableItems="tableItems"
            :vatRate="vatRate"
          />
        </div>

        <div class="hint-bar">
          <span class="hint"><kbd>F2</kbd> or <kbd>Ctrl</kbd>+<kbd>Enter</kbd> Print &amp; Submit</span>
          <span class="hint"><kbd>Enter</kbd> on scan adds item</span>
          <span class="hint"><kbd>Tab</kbd> or <kbd>&uarr;</kbd><kbd>&darr;</kbd> move between fields</span>
          <span class="hint ml-auto">Items <strong>{{ tableItems.length }}</strong></span>
        </div>
      </div>

      <div class="pos-side">
        <PosPaymentPanel
          :tableItems="tableItems"
          :salesAccountHeads="salesAccountHeads"
          :rewardPointsAccountHeads="rewardPointsAccountHeads"
          :cashAndBank="cashAndBank"
          :isSubmitting="submitLoading"
          @clear="resetForm"
          @newBill="newBill"
          @submit="handleSubmit"
          ref="billingSummary"
        />
      </div>
    </div>

    <Loader v-if="loader"/>

    <div ref="posReceipt">
      <teleport to="body">
        <div id="pos-print-area-portal">
          <PosReceiptPrint
              :company="company"
              :invoice="invoiceRes"
              :posPrintSize="posPrintSize"
          />
        </div>
      </teleport>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, onUnmounted, provide, ref } from 'vue';
import ListTable from '@/components/atom/super-shop/ListTable.vue';
import TableHeader from '@/components/atom/super-shop/TableHeader.vue';
import PosPaymentPanel from '@/components/atom/super-shop/PosPaymentPanel.vue';
import PosReceiptPrint from "@/components/molecule/company/pos-invoice/PosReceiptPrint.vue";
import { useRoute, useRouter } from 'vue-router';
import handlePurchase from '@/services/modules/purchase';
import { vatRate } from '@/data/inventory.js';
import handleCompany from "@/services/modules/company";
import pdfPrinter from '@/services/utils/pdfPrinter';
import useDate from "@/services/utils/day";
import Loader from '@/components/atom/LoaderComponent';
import figureFormatter from "@/services/utils/figureFormatter";
import handleBarcode from "@/services/modules/barcode";
import handleDropdowns from '@/services/modules/dropdowns';
import handleBusinessesLocations from '@/services/modules/businessesLocations';
import { useStore } from "vuex";

const { exportToPDF } = pdfPrinter();
const { fetchSalesBill, ...rest } = handlePurchase();
const { fetchCompanyInfo, fetchActiveCustomizationField, getCompanyPreferences } = handleCompany();
const { formatDate } = figureFormatter();
const { fetchBarcode } = handleBarcode();
const { fetchDropdownList } = handleDropdowns();
const { fetchBusinessLocationsList } = handleBusinessesLocations();

const $route = useRoute();
const $router = useRouter();
const store = useStore();
const date = useDate();

const companyId = $route.params.companyId;
const todayDate = date.currentDate();
const loader = ref(true);
const submitLoading = ref(false);
const formHeader = ref(null);
const salesAccountHeads = ref([]);
const totals = ref({});
const billingSummary = ref(null);
const invoiceRes = ref({});
const company = ref({});
const barcodeQuery = ref('');
const barcode = ref('');
const qrcode = ref('');
const customizationPdfFields = ref([]);
const cashAndBank = ref([]);
const rewardPointsAccountHeads = ref([]);
const posPrintSize = ref(58);
const posReceipt = ref(null);
const locations = ref([]);
let tableItems = ref([]);
let formData = ref({
  company_id: companyId,
  contact_profile_id: null,
  referrer_id: null,
  account_head_id: null,
  ipd_register_id: null,
  vat_payable_account_head_id: null,
  project_id: null,
  cost_centre_id: null,
  location_id: null,
  mop_account_head_id: null,
  discount_head_id: null,
  service_resource_id: null,
  receipt_reference: null,
  adjustment_amount: null,
  receipt_description: "Pos bill payment",
  payment: null,
  status: 'active',
  date: '',
  has_item_detail: true,
  description: '',
  order_number: null,
  sales_person_id: null,
  commission_percent: null,
  lc_number: null,
  item_details: [],
  points_redeemed: 0
});
provide('formData', formData);
provide('totals', totals);
const showSuccess =  inject('showSuccess');
const showError =  inject('showError');

const additionalProductQuery = computed(() => {
  return {
    location_id: formData.value.location_id,
    with_desc_stock: 1
  }
})

onMounted(() => {
  loader.value=true
  const dropdownRes = fetchDropdownList(getDropDownQuery());

  Promise.all([
    dropdownRes.then(res => {
      if(! res.status) return;

      if(res.data.account_head_list_by_slag.accounts_receivable) {
        formData.value.account_head_id = res.data.account_head_list_by_slag.accounts_receivable[0].id;
      }

      if(res.data.account_head_list_by_slag.vat_payable) {
        formData.value.vat_payable_account_head_id = res.data.account_head_list_by_slag.vat_payable[0].id;
      }

      if(res.data.account_head_list_by_slag.discount_account) {
        salesAccountHeads.value = res.data.account_head_list_by_slag.discount_account;
      }

      if(res.data.account_head_list_by_slag.cash_and_banks) {
        cashAndBank.value = res.data.account_head_list_by_slag.cash_and_banks;
        formData.value.mop_account_head_id = cashAndBank.value[0].id;
      }

      if(res.data.account_head_list_by_slag.reward_points) {
        rewardPointsAccountHeads.value = res.data.account_head_list_by_slag.reward_points;
      }
    }),
    fetchCompanyInfo(companyId).then(res=> {
      if(res.data) company.value = res.data;
    }),
    getActiveFiledCustomization(),
    getPosPrintSizeData(),
    getLocations()
  ]).finally(() => {loader.value=false});

  window.addEventListener('keyup', handleGlobalKeyup);
  document.body.style.overflow = 'hidden';
})

onUnmounted(() => {
  window.removeEventListener('keyup', handleGlobalKeyup);
  document.body.style.overflow = '';
});

const handleGlobalKeyup = (event) => {
  if (event.key === 'F2' || (event.key === 'Enter' && event.ctrlKey)) {
    event.preventDefault();
    handleSubmit();
  }
}

const getPosPrintSizeData = async () => {
  const queryObject = {
    company_id: companyId,
    fields: 'pos_print_size'
  }
  const query = '?' + new URLSearchParams(queryObject).toString();

  await getCompanyPreferences(query)
      .then(res => {
        if (res.status){
          const data = res.data;
          posPrintSize.value = getInitialValue(data, 'pos_print_size')
        }
      });
}

const getInitialValue = (data, field) => {
  const item = data.find((item => item.field === field))
  return item ? item.value : null;
}

const getDropDownQuery = () => {
  let query = `?company_id=${companyId}
    &is_account_heads=1
    &slags[]=discount_account
    &slags[]=reward_points
    &slags[]=vat_payable
    &slags[]=accounts_receivable
    &slags[]=cash_and_banks`;

  return query;
}

const getActiveFiledCustomization = async () => {
  const companyQuery = `?company_id=${companyId}&customization_type=pdf&type=supershop_pos_pdf`;
  const res = await fetchActiveCustomizationField(companyQuery);
  customizationPdfFields.value = res.data;
};

const getLocations = async () => {
  const locationQuery = `?company_id=${companyId}&location_type=fg_warehouse`;
  const res = await fetchBusinessLocationsList(locationQuery);
  if (! res.data || ! res.data.length) return;
  locations.value = res.data;
  formData.value.location_id = res.data[0].id;
};

const goToList = () => {
  $router.push({name: 'supershop-pos-list',
    params: {
      companyId: $route.params.companyId,
      moduleId: $route.params.moduleId,
      menuId: $route.params.menuId,
      pageId: $route.params.pageId
    },
    query: {
      start: $route.query.start,
      end: $route.query.end
    }
  })
}

const resetForm = () => {
  const accountHeadId = formData.value.account_head_id
  const vatPayableAccountHeadId = formData.value.vat_payable_account_head_id
  const mopAccountHeadId = formData.value.mop_account_head_id;
  const locationId = formData.value.location_id;
  formData.value = {
    company_id: companyId,
    contact_profile_id: null,
    referrer_id: null,
    ipd_register_id: null,
    project_id: null,
    account_head_id: accountHeadId,
    cost_centre_id: null,
    location_id: locationId,
    mop_account_head_id: mopAccountHeadId,
    vat_payable_account_head_id: vatPayableAccountHeadId,
    discount_head_id: null,
    service_resource_id: null,
    receipt_reference: null,
    adjustment_amount: null,
    receipt_description: "Pos bill payment",
    payment: null,
    status: 'active',
    date: '',
    has_item_detail: true,
    description: '',
    order_number: null,
    sales_person_id: null,
    commission_percent: null,
    lc_number: null,
    item_details: [],
    points_redeemed: 0
  };
  tableItems.value = [];
  totals.value = {
    subTotalVal: 0,
    totalVatVal: 0,
    totalVal: 0,
    grandTotalVal: 0
  };
  formHeader.value.resetData();
  if (billingSummary.value) {
    billingSummary.value.resetData();
  }
}

const newBill = () => {
  const newTabUrl = $router.resolve({
    name: 'supershop-pos',
    params: {
      companyId: $route.params.companyId,
      moduleId: $route.params.moduleId,
      menuId: $route.params.menuId,
      pageId: $route.params.pageId
    },
    query: {
      start: $route.query.start,
      end: $route.query.end
    }
  }).href;

  window.open(newTabUrl, '_blank');
}

const handleSubmit = () => {
  if(! formData.value.contact_profile_id) {
    return showError('Please select customer');
  }
  if(! tableItems.value.length) {
    return showError('Please add at least one product');
  }
  if(billingSummary.value && ! billingSummary.value.isPaymentValid()) return;
  if(submitLoading.value) return;

  submitData();
}

const submitData = () => {
  submitLoading.value = true;

  if(!formData.value.date) formData.value.date = date.currentDate();
  let copyFormData = Object.assign({}, formData.value);
  copyFormData.item_details = tableItems.value;
  copyFormData.sale_type = "pos_invoice"
  copyFormData.receipt_type = "pos_invoice"

  rest.storeBillPayment(copyFormData)
    .then(res => {
      if(! res.status) {
        showError(res.message);
        submitLoading.value = false;
        return;
      }
      submitLoading.value = false;
      tableItems.value = [];
      resetForm();
      showSuccess(res.message)
      goToPrint(res.data.id)
    })
    .catch(err=> {
      showError('Something went wrong')
      submitLoading.value = false;
  })
}

const goToPrint = async (id) => {
  const user = store.state.user.user
  const companyQuery = `?company_id=${companyId}`;
  const posPrint = customizationPdfFields.value && customizationPdfFields.value.heading_left_pos_print;

  try {
    const res = await fetchSalesBill(id, companyQuery);
    if (!res) return;

    invoiceRes.value = res.data;
    let qrcodeData = JSON.stringify({
      ref_no: invoiceRes.value.bill_number,
      ref_date: invoiceRes.value.date,
      created_by: user.name,
      created_date_time: formatDate(new Date())
    })
    barcodeQuery.value = `?code=${invoiceRes.value.bill_number}&qrcode=${qrcodeData}`;

    const barcodeRes = await fetchBarcode(barcodeQuery.value);
    barcode.value = barcodeRes.barcode;
    qrcode.value = barcodeRes.qrcode;

    if( posPrint ) {
      window.print();
      return;
    }
    exportToPDF(company.value, invoiceRes.value, barcode.value, qrcode.value, user.name, 1)
  } catch (e) {
    showError("Something went wrong during printing");
  }
}

</script>

<style scoped>
.pos-screen {
  background: #f2f4f7;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1035;
  overflow: hidden;
  padding: 10px;
  display: flex;
  flex-direction: column;
}
.pos-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e5e9f0;
  border-radius: 12px;
  padding: 8px 12px;
  margin-bottom: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.brand-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, #0a4d8c, #1a75d2);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}
.brand-title {
  font-size: 17px;
  font-weight: 700;
  color: #101828;
  white-space: nowrap;
}
.topbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.warehouse-select {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 220px;
}
.warehouse-icon {
  color: #98a2b3;
  font-size: 14px;
  flex-shrink: 0;
}
.warehouse-select :deep(.v-select) {
  flex: 1;
  min-width: 0;
}
.warehouse-select :deep(.vs__dropdown-toggle) {
  border-radius: 8px;
  border-color: #d0d5dd;
  min-height: 34px;
}
.warehouse-select :deep(.vs__selected) {
  font-size: 13px;
  font-weight: 600;
  color: #101828;
}
.date-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f2f4f7;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #475467;
}
.date-chip i {
  color: #98a2b3;
}
.exit-btn {
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #475467;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
}
.exit-btn:hover {
  color: #b42318;
  border-color: #fda29b;
  background: #fef3f2;
}
.pos-body {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 350px;
  gap: 10px;
}
.pos-main {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  min-height: 0;
}
.pos-side {
  min-height: 0;
  overflow-y: auto;
  border-radius: 10px;
}
.entry-card {
  background: #ffffff;
  border: 1px solid #e5e9f0;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
.items-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e5e9f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
.items-card :deep(.pos-items) {
  flex: 1;
}
.hint-bar {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  background: #101828;
  color: #98a2b3;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 11px;
  letter-spacing: 0.02em;
}
.hint-bar .hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.hint-bar kbd {
  background: #344054;
  border: 1px solid #475467;
  border-bottom-width: 2px;
  border-radius: 4px;
  padding: 0 5px;
  color: #ffffff;
  font-size: 10px;
  font-family: inherit;
}
.hint-bar strong {
  color: #a6f4c5;
  font-size: 13px;
}
@media (max-width: 1200px) {
  .pos-screen {
    overflow-y: auto;
  }
  .pos-body {
    grid-template-columns: minmax(0, 1fr);
  }
  .pos-side {
    overflow-y: visible;
  }
  .items-card {
    min-height: 320px;
  }
}
address{
  margin-bottom: 0 !important;
}
.template-selection {
  margin-left: -97%;
  width: 166%;
}
</style>
